import { WhoAmIResponse } from '../api/authAPI';
import { User, UserRole } from '../types/user';

/**
 * Maps the custom role from the API response to our UserRole enum.
 *
 * HA-309: comparison is case-insensitive. Cognito stores `custom:role`
 * in PascalCase (`Practitioner`, `PracticeAdmin`, `Client`, `SuperUser`
 * — see business/Healio.Security/Role.cs). Older/seeded data may use
 * camelCase or lowercase (`practiceAdmin`, `practitioner`). Comparing
 * the lowercase form covers all variants.
 *
 * Cognito's `SuperUser` and the UI's `SystemAdmin` are the same role
 * conceptually; both aliases map to UserRole.SystemAdmin.
 *
 * Unknown values fall back to UserRole.Client and emit a console warning
 * so the silent demotion that triggered HA-309 cannot recur unobserved.
 */
export const mapApiRoleToUserRole = (customRole: string | undefined | null): UserRole => {
  switch (customRole?.toLowerCase()) {
    case 'practiceadmin':
      return UserRole.PracticeAdmin;
    case 'practitioner':
      return UserRole.Practitioner;
    case 'client':
      return UserRole.Client;
    case 'systemadmin':
    case 'superuser':
      return UserRole.SystemAdmin;
    default:
      console.warn(
        `mapApiRoleToUserRole: unknown role "${customRole}", defaulting to Client (HA-309 guard)`
      );

      return UserRole.Client;
  }
};

/**
 * HA-446: roles that belong to no practice at all, so every practice-scoped endpoint
 * (practice status, the practice's license, its profile) refuses them. Today that is only
 * SystemAdmin. The app-wide providers ask this one question, so they can't drift apart on it.
 */
export const hasNoPractice = (role: UserRole | undefined | null): boolean =>
  role === UserRole.SystemAdmin;

/**
 * Converts WhoAmI API response to User object
 * @param userData WhoAmI API response
 * @returns User object
 */
export const mapWhoAmIResponseToUser = (userData: WhoAmIResponse): User => {
  return {
    id: userData.sub,
    email: userData.email,
    firstName: userData.given_name,
    lastName: userData.family_name,
    role: mapApiRoleToUserRole(userData['custom:role']),
    username: userData.username,
    phoneNumber: userData.phone_number,
    birthdate: userData.birthdate,
    practiceName: userData['custom:practice_name'],
    emailVerified: userData.email_verified,
    phoneNumberVerified: userData.phone_number_verified,
  };
};

/**
 * Extracts the practice ID from a User object
 * The practice ID is the practice name from the JWT (custom:practice_name)
 * @param user User object from AuthContext
 * @returns Practice ID string or null if not available
 */
export const getPracticeIdFromUser = (user: User | null): string | null => {
  return user?.practiceName || null;
};

/**
 * Extracts practice ID from current authentication context
 * @param currentUser User from useAuth hook
 * @returns Practice ID for DynamoDB operations
 */
export const getCurrentPracticeId = (currentUser: User | null): string => {
  const practiceId = getPracticeIdFromUser(currentUser);

  if (!practiceId) {
    throw new Error(
      'No practice ID found for current user. User may not be properly authenticated.'
    );
  }

  return practiceId;
};
